import { Icon } from '@natera/material/lib/icon'
import { useStore } from '@tanstack/react-form'
import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSubcategories } from '~/entities/categories'
import type { DataExtractionFieldName } from '~/entities/document'
import {
  DOCUMENT_SERVER_STATUS,
  getDocumentStepUrl,
  getDocumentsUrlWithCurrentParams,
  getIsPatientFinished,
  isTaggingClassification,
  useClassificationReviews,
  useCurrentDocument,
} from '~/entities/document'
import { FLAG_TYPES, useStepFlagState } from '~/entities/document-flags'
import { useAuditTrail, useAuditTrailQueryParams } from '~/features/audit-trail'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { cn } from '~/shared/lib/cn'
import { formatCurrency } from '~/shared/lib/currency'
import { formatDateDefault } from '~/shared/lib/datetime'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { withForm } from '~/shared/lib/form'
import { useElementWidth } from '~/shared/lib/use-element-width'
import { useIsScrolled } from '~/shared/lib/use-is-scrolled'
import {
  Button,
  IconButton,
  InfoTip,
  LocalIcon,
  OptiWalkingWithCheckmark,
  RadioButton,
  ScrollArea,
  SearchDocIllustration,
  Text,
  Textarea,
  Textfield,
} from '~/shared/ui'
import { ReviewActions } from '~/widgets/review-actions'
import { usePatientMatches } from '../../api'
import { NO_DATA_PLACEHOLDER } from '../../config'
import type { HoveredField, PatientMatch } from '../../model'
import { ClaimMatchesSection, ClaimMatchesSectionHeader } from '../claim-matches'
import { PaMatchesSection } from '../pa-matches'
import { ConfidenceBadge } from './confidence-badge'
import { LoadingSkeleton as NewMatchLoadingSkeleton } from './new-match/loading-skeleton'
import { NewMatch } from './new-match/new-match'

function isMatchCreatedByUser(match: PatientMatch) {
  return !match.confidence_scores?.confidence_score
}

function parseCents(value: string | null | undefined): number | null {
  if (!value) return null
  const cleaned = value
    .split(/,\s+/)[0]
    .trim()
    .replace(/[^0-9.-]/g, '')
  if (!cleaned) return null
  const parsed = Number(cleaned)
  if (Number.isNaN(parsed)) return null
  return Math.round(parsed * 100)
}

const CARD_MIN_WIDTH = 240
const CARD_GAP = 12
// Inline mode: grid is `pl-8 pr-8` ⇒ 64 px outside the cards.
const INLINE_GRID_PADDING = 64
// Pinned mode: grid is `pl-8 pr-3` (32+12=44), then a 48 px button with `mr-8`
// (32) sitting outside the ScrollArea ⇒ 124 px outside the cards.
const PINNED_ROW_RESERVED = 124
const PINNED_ADD_WIDTH = 48
const FALLBACK_INLINE_CARDS = 3

type FormValues = {
  match: {
    matchStatus: apiClient.MatchStatus | null
    selectedMatchIndex: number | undefined
    limsCaseId: string
    visitId: string
  }
  hiddenMatchIndexes: number[]
  notes: string
  newlyAddedMatch: PatientMatch | null
}

export const Patients = withForm({
  defaultValues: {} as FormValues,
  props: {
    isPatientMatchingReviewError: false,
    historyValues: {} as FormValues,
    setHistoryValues: (_values: Partial<FormValues>) => {},
    revert: () => {},
    undo: () => {},
    redo: () => {},
    canUndo: false,
    canRedo: false,
    hoveredField: null as HoveredField,
    documentFields: {} as Record<DataExtractionFieldName, string | undefined>,
    autoAcceptPatientMatchIndex: null as number | null,
    paMatchesData: undefined as apiClient.PatientPaCaseCandidatesResponse | undefined,
    isPaMatchingCutover: false,
    isPaCardRedesignEnabled: false,
    showAllContradictions: false,
    isClaimCardRedesign: false,
    isPaMatchingV2: false,
    isClaimMatchingV2: false,
    resolvedTestNames: null as string | null,
  },
  render: function Render({
    form,
    isPatientMatchingReviewError,
    historyValues,
    setHistoryValues,
    revert,
    undo,
    redo,
    canUndo,
    canRedo,
    hoveredField,
    documentFields,
    autoAcceptPatientMatchIndex,
    paMatchesData,
    isPaMatchingCutover,
    isPaCardRedesignEnabled,
    showAllContradictions,
    isClaimCardRedesign,
    isPaMatchingV2,
    isClaimMatchingV2,
    resolvedTestNames,
  }) {
    const {
      data: { document },
    } = useCurrentDocument()
    const {
      data: {
        patient_data: patientMatches,
        selected_match_index: selectedMatchIndex,
        user_suggested_lims_case_file_id: userSuggestedLimsCaseFileId,
        user_suggested_patient_account_number: userSuggestedPatientAccountNumber,
      },
    } = usePatientMatches({ documentKey: document.key })
    const isReviewStateResetEnabled = useIsFeatureEnabled('2026-07-review-state-reset')
    const {
      data: { items: classificationReviews },
    } = useClassificationReviews({ documentKey: document.key })
    const isFullyReviewed =
      document.review_status === DOCUMENT_SERVER_STATUS.reviewed ||
      document.review_status === DOCUMENT_SERVER_STATUS.auto_reviewed
    const wasReclassified =
      isReviewStateResetEnabled && (classificationReviews?.length ?? 0) >= 2 && !isFullyReviewed
    const subcategories = useSubcategories()
    const isTaggingEnabled = isTaggingClassification(document.category, subcategories)
    const isPeerToPeerEnabled = useIsFeatureEnabled('2026-05-peer-to-peer')
    const isBilledAmountEnabled = useIsFeatureEnabled('2026-05-billed-amount-patient-matching')
    const isCarrierClaimIdEnabled = useIsFeatureEnabled('2026-06-carrier-claim-id')
    const navigate = useNavigate()
    const stepFlag = useStepFlagState(FLAG_TYPES.STEP_PATIENT)

    const formStore = useStore(form.store, (state) => state)

    const patients: PatientMatch[] = useMemo(() => {
      return (formStore.values.newlyAddedMatch ? [formStore.values.newlyAddedMatch] : []).concat(
        (patientMatches || [])
          .map((patient, index) => ({
            ...patient,
            matchIndex: index,
          }))
          // Move selected match to the top, then place created by user matches after it
          .sort((a, b) => {
            if (a.matchIndex === selectedMatchIndex) {
              return -1
            }
            if (b.matchIndex === selectedMatchIndex) {
              return 1
            }
            if (isMatchCreatedByUser(a) && !isMatchCreatedByUser(b)) {
              return -1
            }
            if (!isMatchCreatedByUser(a) && isMatchCreatedByUser(b)) {
              return 1
            }
            return 0
          })
          .slice(0, 25),
      )
    }, [formStore.values.newlyAddedMatch, patientMatches, selectedMatchIndex])

    const connectUrlMap = useMemo(() => {
      const map = new Map<string, string>()
      for (const match of patientMatches ?? []) {
        if (match.connect_patient_service_url) {
          if (match.lims_casefile_id) {
            map.set(match.lims_casefile_id, match.connect_patient_service_url)
          }
          if (match.visitfid) {
            map.set(match.visitfid, match.connect_patient_service_url)
          }
        }
      }
      return map
    }, [patientMatches])

    const [fadingOutIndex, setFadingOutIndex] = useState<number | null>(null)
    const [currentPage, setCurrentPage] = useState(0)
    const [claimCurrentPage, setClaimCurrentPage] = useState(0)

    const rowRef = useRef<HTMLDivElement>(null)
    const rowWidth = useElementWidth(rowRef)
    const cardsScrollViewportRef = useRef<HTMLDivElement>(null)
    const [cardsRowOverflows, setCardsRowOverflows] = useState(false)
    const { isAuditTrailVisible } = useAuditTrail()
    const hasNewlyAddedMatch = !!formStore.values.newlyAddedMatch
    // Hide first/last pagination buttons when AT is open on narrow viewports
    // (< 1440 px). AT consumes 480 px and the left panel 360 px, so workspace
    // width below 600 px ⇒ viewport below 1440 px.
    const hideFirstLastPagination = isAuditTrailVisible && rowWidth > 0 && rowWidth < 600

    // Largest N where N cards (each ≥ 240 px, gap 12 px) fit in the row.
    // Treat width=0 (not yet measured) as a fallback to avoid an empty flicker.
    const inlineUsableWidth = Math.max(0, rowWidth - INLINE_GRID_PADDING)
    const measuredCardsThatFit =
      rowWidth === 0
        ? FALLBACK_INLINE_CARDS
        : Math.max(1, Math.floor((inlineUsableWidth + CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP)))

    // When 3+ cards fit and no match has been added yet, the last grid cell is
    // the inline "Add new match" card (current behavior). Otherwise the add
    // control becomes a compact `+` button pinned to the right of the row.
    const showInlineAdd = !hasNewlyAddedMatch && measuredCardsThatFit >= 3
    const showPinnedAdd = !hasNewlyAddedMatch && !showInlineAdd

    const usableRowWidth = Math.max(
      0,
      rowWidth - (showPinnedAdd ? PINNED_ROW_RESERVED : INLINE_GRID_PADDING),
    )
    const cardsPerPage = Math.max(
      2,
      Math.floor((usableRowWidth + CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP)) -
        (showInlineAdd ? 1 : 0),
    )

    const gridColumnCount = cardsPerPage + (showInlineAdd ? 1 : 0)

    const textFieldRef = useRef<HTMLLabelElement>(null)
    const queryParams = useAuditTrailQueryParams()
    const [isNewMatchVisible, setIsNewMatchVisible] = useState(false)
    const [visiblePatients, setVisiblePatients] = useState<PatientMatch[]>([])

    // Focus LIMS textfield when "No match" is selected
    useEffect(() => {
      if (formStore.values.match.matchStatus === 'no_match' && textFieldRef.current) {
        textFieldRef.current.focus()
      }
    }, [formStore.values.match.matchStatus])

    useEffect(() => {
      const filteredPatients = patients.filter(
        (patient) => !formStore.values.hiddenMatchIndexes.includes(patient.matchIndex),
      )
      setVisiblePatients(filteredPatients)
    }, [patients, formStore.values.hiddenMatchIndexes])

    useEffect(() => {
      const viewport = cardsScrollViewportRef.current
      if (!viewport) {
        setCardsRowOverflows(false)
        return
      }
      const check = () => {
        setCardsRowOverflows(viewport.scrollLeft + viewport.clientWidth < viewport.scrollWidth - 1)
      }
      check()
      const ro = new ResizeObserver(check)
      ro.observe(viewport)
      // Watch the inner content too — Radix wraps children in a sub-element
      // whose size dictates scrollWidth.
      for (const child of Array.from(viewport.children)) {
        ro.observe(child)
      }
      viewport.addEventListener('scroll', check, { passive: true })
      return () => {
        ro.disconnect()
        viewport.removeEventListener('scroll', check)
      }
    }, [showPinnedAdd, cardsPerPage, visiblePatients.length])

    const submitNewMatch = (match: PatientMatch | null) => {
      form.setFieldValue('newlyAddedMatch', match)
      const matchValue = {
        selectedMatchIndex: match?.matchIndex,
        matchStatus: 'validated' as apiClient.MatchStatus,
        limsCaseId: '',
        visitId: '',
      }
      form.setFieldValue('match', matchValue)
      setHistoryValues({
        newlyAddedMatch: match,
        match: matchValue,
      })
      setIsNewMatchVisible(false)
    }

    const { isScrolled: isScrolledTop, sentinelElement: sentinelElementTop } = useIsScrolled()
    const { isScrolled: isScrolledBottom, sentinelElement: sentinelElementBottom } = useIsScrolled()

    return (
      <div
        ref={rowRef}
        className="relative flex max-w-full min-w-0 grow flex-col overflow-hidden"
      >
        {isNewMatchVisible && (
          <div className="bg-surface-lowest border-divider absolute top-0 right-0 z-6 flex h-full w-[428px] flex-col border-l">
            <Suspense fallback={<NewMatchLoadingSkeleton />}>
              <NewMatch
                newlyAddedMatch={formStore.values.newlyAddedMatch}
                onSubmit={submitNewMatch}
                onClose={() => setIsNewMatchVisible(false)}
                isPaMatchingCutover={isPaMatchingCutover && isPaCardRedesignEnabled}
                resolvedTestName={isPaMatchingV2 ? resolvedTestNames : undefined}
              />
            </Suspense>
          </div>
        )}
        {isClaimCardRedesign && (patientMatches ?? []).length > 0 && (
          <div className="shrink-0 px-8 pt-4">
            <ClaimMatchesSectionHeader
              totalCount={(patientMatches ?? []).length}
              containerWidth={rowWidth}
              currentPage={claimCurrentPage}
              onPageChange={setClaimCurrentPage}
              hasAddButton={!hasNewlyAddedMatch}
              hasNewlyAdded={hasNewlyAddedMatch}
            />
          </div>
        )}
        <ScrollArea className="flex-1">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <div
              className={cn('flex flex-col', {
                'h-[526px]': !isPaMatchingCutover && !isClaimCardRedesign && patients.length === 0,
              })}
            >
              {isPaMatchingCutover || isClaimCardRedesign ? null : patients.length === 0 ? (
                <div className="flex grow px-8">
                  <div className="mt-[108px] flex h-[400px] w-full flex-col items-center justify-center pb-4 text-center">
                    <SearchDocIllustration className="text-text-primary" />
                    <div className="mt-6 flex flex-col items-center gap-4">
                      <Text
                        className="text-text-secondary"
                        appearance="H6"
                      >
                        No matches found
                      </Text>
                      <Text
                        className="text-content-high-emphasis w-[380px]"
                        appearance="BODY2"
                      >
                        opti couldn't find a match. Create a new one or choose No Match to proceed.
                      </Text>
                    </div>
                    <Button
                      className="mt-8"
                      materialIcon="add"
                      onClick={() => setIsNewMatchVisible(true)}
                    >
                      Create new match
                    </Button>
                  </div>
                </div>
              ) : (
                <form.Subscribe
                  selector={(state) => ({
                    hiddenMatchIndexes: state.values.hiddenMatchIndexes,
                  })}
                  children={({ hiddenMatchIndexes }) => {
                    const totalPages = Math.ceil(visiblePatients.length / cardsPerPage)
                    const page = Math.min(currentPage, Math.max(0, totalPages - 1))
                    const paginatedPatients = visiblePatients.slice(
                      page * cardsPerPage,
                      (page + 1) * cardsPerPage,
                    )

                    // If currentPage is out of bounds due to hiding, reset to last valid page
                    if (currentPage !== page) {
                      setTimeout(() => setCurrentPage(page), 0)
                    }

                    return (
                      <>
                        {sentinelElementTop}
                        <div
                          className={cn(
                            'bg-surface-lowest sticky top-0 z-1 mb-1 flex items-start justify-between gap-4 px-8 pt-4 pb-3 whitespace-nowrap',
                            {
                              'shadow-popover transition-shadow': isScrolledTop,
                            },
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <Text appearance="H6">Select a Match</Text>
                            <InfoTip
                              iconSize="24"
                              align="end"
                            >
                              <div className="flex max-w-[245px] flex-col gap-2">
                                <OptiWalkingWithCheckmark width="50" />
                                <Text
                                  className="text-text-secondary"
                                  appearance="CAPTION"
                                >
                                  I searched Natera's systems and pulled these patient records for
                                  comparison and selection.
                                </Text>
                              </div>
                            </InfoTip>
                          </div>
                          <div className="flex items-center gap-4">
                            <Text appearance="BODY2">
                              {page * cardsPerPage + 1} -{' '}
                              {Math.min((page + 1) * cardsPerPage, visiblePatients.length)} of{' '}
                              {visiblePatients.length}
                            </Text>
                            {!hideFirstLastPagination && (
                              <IconButton
                                size="medium"
                                container="small"
                                materialIcon="first_page"
                                onClick={() => setCurrentPage(0)}
                                disabled={page === 0}
                                aria-label="First page"
                              />
                            )}
                            <IconButton
                              size="medium"
                              container="small"
                              materialIcon="chevron_left"
                              onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                              disabled={page === 0}
                              aria-label="Previous page"
                            />
                            <IconButton
                              size="medium"
                              container="small"
                              materialIcon="chevron_right"
                              onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                              disabled={page >= totalPages - 1}
                              aria-label="Next page"
                            />
                            {!hideFirstLastPagination && (
                              <IconButton
                                size="medium"
                                container="small"
                                materialIcon="last_page"
                                onClick={() => setCurrentPage(totalPages - 1)}
                                disabled={page >= totalPages - 1}
                                aria-label="Last page"
                              />
                            )}
                          </div>
                        </div>

                        <div
                          className="relative flex min-w-0"
                          style={{ width: rowWidth || '100%' }}
                        >
                          <ScrollArea
                            className="min-w-0 grow"
                            orientation="horizontal"
                            viewportRef={cardsScrollViewportRef}
                          >
                            <div
                              className={cn('grid gap-3 pb-3 pl-8', {
                                'pr-8': !showPinnedAdd,
                                'pr-3': showPinnedAdd,
                              })}
                              style={{
                                gridTemplateColumns: `repeat(${gridColumnCount}, minmax(${CARD_MIN_WIDTH}px, 1fr))`,
                              }}
                            >
                              {paginatedPatients.map((patientMatch) => {
                                const confidencePercentage = Math.round(
                                  (patientMatch.confidence_scores?.confidence_score ?? 0) * 100,
                                )
                                const isFadingOut = fadingOutIndex === patientMatch.matchIndex
                                return (
                                  <div
                                    key={patientMatch.matchIndex}
                                    className={cn(
                                      'border-divider overflow-hidden rounded-lg border whitespace-nowrap transition-opacity duration-300',
                                      {
                                        'pointer-events-none opacity-0': isFadingOut,
                                        'border-primary-main': patientMatch.newlyAdded,
                                        'border-chip-status-success-content':
                                          autoAcceptPatientMatchIndex === patientMatch.matchIndex,
                                      },
                                    )}
                                  >
                                    <form.Field
                                      name="match"
                                      children={(field) => {
                                        function handleChange() {
                                          if (
                                            field.state.value.selectedMatchIndex !==
                                            patientMatch.matchIndex
                                          ) {
                                            field.handleChange({
                                              ...field.state.value,
                                              matchStatus: 'validated',
                                              selectedMatchIndex: patientMatch.matchIndex,
                                            })
                                            setHistoryValues({
                                              match: {
                                                ...field.state.value,
                                                matchStatus: 'validated',
                                                selectedMatchIndex: patientMatch.matchIndex,
                                              },
                                            })
                                          }
                                        }

                                        return (
                                          <div className="border-divider h-12 border-b">
                                            <button
                                              className="bg-surface-lowest relative flex h-full w-full cursor-pointer items-center justify-between border-none px-4 py-0"
                                              type="button"
                                              data-track-name="patient_match"
                                              onClick={handleChange}
                                            >
                                              <RadioButton
                                                label={
                                                  patientMatch.newlyAdded ||
                                                  isMatchCreatedByUser(patientMatch)
                                                    ? 'Added by user'
                                                    : 'Match'
                                                }
                                                withinButton
                                                checked={
                                                  field.state.value.selectedMatchIndex ===
                                                  patientMatch.matchIndex
                                                }
                                                onChange={handleChange}
                                              />
                                              {autoAcceptPatientMatchIndex ===
                                                patientMatch.matchIndex && (
                                                <span className="text-success-content absolute top-[calc(50%+6px)] left-[42px] text-[11px] leading-[16px] font-medium">
                                                  opti suggested
                                                </span>
                                              )}
                                              {confidencePercentage ? (
                                                <ConfidenceBadge
                                                  confidencePercentage={confidencePercentage}
                                                />
                                              ) : null}
                                            </button>
                                          </div>
                                        )
                                      }}
                                    />

                                    <Text
                                      className="px-4 pt-3"
                                      appearance="OVERLINE"
                                    >
                                      {patientMatch.newlyAdded || isMatchCreatedByUser(patientMatch)
                                        ? 'Payor document data'
                                        : 'Natera patient data'}
                                    </Text>
                                    <div className="mt-3 flex flex-col gap-2 px-4">
                                      <div className="flex h-5 items-center justify-between gap-2">
                                        <Text
                                          appearance="SUBTITLE"
                                          placeholder={NO_DATA_PLACEHOLDER}
                                          truncated
                                          withTooltip
                                          copyable
                                        >
                                          {patientMatch.patient_first_name}{' '}
                                          {patientMatch.patient_last_name}
                                        </Text>
                                      </div>
                                      <div
                                        className={cn('flex transition-colors', {
                                          'bg-secondary-focused':
                                            hoveredField === 'birth_date' &&
                                            patientMatch.date_of_birth &&
                                            documentFields.member_dob &&
                                            formatDateDefault(patientMatch.date_of_birth) ===
                                              formatDateDefault(documentFields.member_dob),
                                          'bg-error-focused':
                                            hoveredField === 'birth_date' &&
                                            (!patientMatch.date_of_birth ||
                                              !documentFields.member_dob ||
                                              formatDateDefault(patientMatch.date_of_birth) !==
                                                formatDateDefault(documentFields.member_dob)),
                                        })}
                                      >
                                        <Text
                                          className="text-text-secondary"
                                          appearance="BODY2"
                                          placeholder={NO_DATA_PLACEHOLDER}
                                          truncated
                                          withTooltip
                                          copyable
                                        >
                                          {patientMatch.date_of_birth
                                            ? formatDateDefault(patientMatch.date_of_birth)
                                            : null}
                                        </Text>
                                      </div>
                                      <div
                                        className={cn('flex transition-colors', {
                                          'bg-secondary-focused':
                                            hoveredField === 'insurance_company' &&
                                            patientMatch.payorname ===
                                              documentFields.insurance_company,
                                          'bg-error-focused':
                                            hoveredField === 'insurance_company' &&
                                            patientMatch.payorname !==
                                              documentFields.insurance_company,
                                        })}
                                      >
                                        <Text
                                          className="text-text-secondary"
                                          placeholder={NO_DATA_PLACEHOLDER}
                                          appearance="BODY2"
                                          truncated
                                          withTooltip
                                          copyable
                                        >
                                          {patientMatch.payorname}
                                        </Text>
                                      </div>
                                      <div
                                        className={cn('flex transition-colors', {
                                          'bg-secondary-focused':
                                            hoveredField === 'test_name' &&
                                            patientMatch.test_name === documentFields.test_name,
                                          'bg-error-focused':
                                            hoveredField === 'test_name' &&
                                            patientMatch.test_name !== documentFields.test_name,
                                        })}
                                      >
                                        <Text
                                          className="text-text-secondary"
                                          placeholder={NO_DATA_PLACEHOLDER}
                                          appearance="BODY2"
                                          truncated
                                          withTooltip
                                          copyable
                                        >
                                          {patientMatch.test_name}
                                        </Text>
                                      </div>
                                      {isPeerToPeerEnabled && (
                                        <div
                                          className={cn('flex transition-colors', {
                                            'bg-secondary-focused':
                                              hoveredField === 'cpt_code' &&
                                              patientMatch.allproccodes_claim ===
                                                documentFields.cpt_code,
                                            'bg-error-focused':
                                              hoveredField === 'cpt_code' &&
                                              patientMatch.allproccodes_claim !==
                                                documentFields.cpt_code,
                                          })}
                                        >
                                          <Text
                                            className="text-text-secondary"
                                            placeholder={NO_DATA_PLACEHOLDER}
                                            appearance="BODY2"
                                            truncated
                                            withTooltip
                                            copyable
                                          >
                                            {patientMatch.allproccodes_claim?.replace(/;/g, ', ')}
                                          </Text>
                                        </div>
                                      )}
                                      <div
                                        className={cn('flex transition-colors', {
                                          'bg-secondary-focused':
                                            hoveredField === 'date_of_service' &&
                                            patientMatch.lims_date_of_service &&
                                            documentFields.service_date_from &&
                                            formatDateDefault(patientMatch.lims_date_of_service) ===
                                              formatDateDefault(documentFields.service_date_from),
                                          'bg-error-focused':
                                            hoveredField === 'date_of_service' &&
                                            (!patientMatch.lims_date_of_service ||
                                              !documentFields.service_date_from ||
                                              formatDateDefault(
                                                patientMatch.lims_date_of_service,
                                              ) !==
                                                formatDateDefault(
                                                  documentFields.service_date_from,
                                                )),
                                        })}
                                      >
                                        <Text
                                          className="text-text-secondary"
                                          appearance="BODY2"
                                          placeholder={NO_DATA_PLACEHOLDER}
                                          truncated
                                          withTooltip
                                          copyable
                                        >
                                          {patientMatch.lims_date_of_service
                                            ? formatDateDefault(patientMatch.lims_date_of_service)
                                            : null}
                                        </Text>
                                      </div>
                                      {isBilledAmountEnabled && (
                                        <div
                                          className={cn('flex transition-colors', {
                                            'bg-secondary-focused':
                                              hoveredField === 'billprice' &&
                                              parseCents(patientMatch.billprice) != null &&
                                              parseCents(patientMatch.billprice) ===
                                                parseCents(documentFields.total_amount),
                                            'bg-error-focused':
                                              hoveredField === 'billprice' &&
                                              (parseCents(patientMatch.billprice) == null ||
                                                parseCents(patientMatch.billprice) !==
                                                  parseCents(documentFields.total_amount)),
                                          })}
                                        >
                                          <Text
                                            className="text-text-secondary"
                                            placeholder={NO_DATA_PLACEHOLDER}
                                            appearance="BODY2"
                                            truncated
                                            withTooltip
                                            copyable
                                          >
                                            {formatCurrency(patientMatch.billprice)}
                                          </Text>
                                        </div>
                                      )}
                                    </div>

                                    {patientMatch.newlyAdded ||
                                    isMatchCreatedByUser(patientMatch) ? (
                                      <div className="relative">
                                        <div className="border-divider my-5 border-b" />
                                        <Text
                                          className="bg-surface-lowest absolute top-0 left-0 ml-3 -translate-y-[calc(50%-1px)] px-1"
                                          appearance="OVERLINE"
                                        >
                                          Natera patient data
                                        </Text>
                                      </div>
                                    ) : (
                                      <div className="border-divider mx-4 my-5 border-b" />
                                    )}

                                    <div className="border-divider flex flex-col gap-2 border-b px-4 pb-3">
                                      <div
                                        className={cn('flex transition-colors', {
                                          'bg-secondary-focused':
                                            hoveredField ===
                                              (isCarrierClaimIdEnabled
                                                ? 'natera_claim_id'
                                                : 'claim_number') &&
                                            patientMatch.claim_id ===
                                              (isCarrierClaimIdEnabled
                                                ? documentFields.natera_claim_id
                                                : documentFields.claim_number),
                                          'bg-error-focused':
                                            hoveredField ===
                                              (isCarrierClaimIdEnabled
                                                ? 'natera_claim_id'
                                                : 'claim_number') &&
                                            patientMatch.claim_id !==
                                              (isCarrierClaimIdEnabled
                                                ? documentFields.natera_claim_id
                                                : documentFields.claim_number),
                                        })}
                                      >
                                        <Text
                                          className="text-text-secondary"
                                          placeholder={NO_DATA_PLACEHOLDER}
                                          appearance="BODY2"
                                          truncated
                                          withTooltip
                                          copyable
                                        >
                                          {patientMatch.claim_id}
                                        </Text>
                                      </div>
                                      {isCarrierClaimIdEnabled && (
                                        <div
                                          className={cn('flex transition-colors', {
                                            'bg-secondary-focused':
                                              hoveredField === 'carrier_claim_id' &&
                                              patientMatch.carrier_claim_id ===
                                                documentFields.claim_number,
                                            'bg-error-focused':
                                              hoveredField === 'carrier_claim_id' &&
                                              patientMatch.carrier_claim_id !==
                                                documentFields.claim_number,
                                          })}
                                        >
                                          <Text
                                            className="text-text-secondary"
                                            placeholder={NO_DATA_PLACEHOLDER}
                                            appearance="BODY2"
                                            truncated
                                            withTooltip
                                            copyable
                                          >
                                            {patientMatch.carrier_claim_id}
                                          </Text>
                                        </div>
                                      )}
                                      <div
                                        className={cn('flex transition-colors', {
                                          'bg-secondary-focused':
                                            hoveredField === 'patient_account_number' &&
                                            patientMatch.visitfid ===
                                              documentFields.patient_account_number,
                                          'bg-error-focused':
                                            hoveredField === 'patient_account_number' &&
                                            patientMatch.visitfid !==
                                              documentFields.patient_account_number,
                                        })}
                                      >
                                        <Text
                                          className="text-text-secondary"
                                          placeholder={NO_DATA_PLACEHOLDER}
                                          appearance="BODY2"
                                          truncated
                                          withTooltip
                                          copyable
                                        >
                                          {patientMatch.visitfid}
                                        </Text>
                                      </div>
                                      <div
                                        className={cn('flex transition-colors', {
                                          'bg-secondary-focused':
                                            hoveredField === 'case_number' &&
                                            patientMatch.lims_casefile_id ===
                                              documentFields.case_number,
                                          'bg-error-focused':
                                            hoveredField === 'case_number' &&
                                            patientMatch.lims_casefile_id !==
                                              documentFields.case_number,
                                        })}
                                      >
                                        <Text
                                          className="text-text-secondary"
                                          placeholder={NO_DATA_PLACEHOLDER}
                                          appearance="BODY2"
                                          truncated
                                          withTooltip
                                          copyable
                                        >
                                          {patientMatch.lims_casefile_id}
                                        </Text>
                                      </div>
                                      <div
                                        className={cn('flex transition-colors', {
                                          'bg-secondary-focused':
                                            hoveredField === 'member_id' &&
                                            patientMatch.policy_number?.replace(/[[\]]/g, '') ===
                                              documentFields.member_id,
                                          'bg-error-focused':
                                            hoveredField === 'member_id' &&
                                            patientMatch.policy_number?.replace(/[[\]]/g, '') !==
                                              documentFields.member_id,
                                        })}
                                      >
                                        <Text
                                          className="text-text-secondary"
                                          placeholder={NO_DATA_PLACEHOLDER}
                                          appearance="BODY2"
                                          truncated
                                          withTooltip
                                          copyable
                                        >
                                          {patientMatch.policy_number}
                                        </Text>
                                      </div>
                                    </div>

                                    <div className="border-divider grid h-[84px] grid-cols-2 items-center gap-2 border-b px-4 py-3 whitespace-normal">
                                      <Text
                                        className="text-text-secondary"
                                        appearance="BODY2"
                                      >
                                        Prior Auth Vendor
                                      </Text>
                                      <div className="flex w-full justify-end">
                                        <Text
                                          className="line-clamp-3"
                                          appearance="BODY2"
                                          title={patientMatch.pa_vendor || ''}
                                        >
                                          {patientMatch.pa_vendor || '--'}
                                        </Text>
                                      </div>
                                    </div>

                                    <div className="flex items-center justify-between p-4">
                                      {patientMatch.newlyAdded ? (
                                        <button
                                          className="text-text-link flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 no-underline"
                                          type="button"
                                          onClick={() => setIsNewMatchVisible(true)}
                                        >
                                          <Text appearance="BODY2">View</Text>
                                          <Icon
                                            children="visibility"
                                            className="text-[18px]"
                                          />
                                        </button>
                                      ) : !isMatchCreatedByUser(patientMatch) ? (
                                        <a
                                          className="text-text-link flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 no-underline"
                                          target="_blank"
                                          href={patientMatch.connect_patient_service_url}
                                        >
                                          <Text appearance="BODY2">More</Text>
                                          <LocalIcon
                                            name="openInNew"
                                            size="18"
                                          />
                                        </a>
                                      ) : (
                                        <span />
                                      )}
                                      {!patientMatch.newlyAdded &&
                                        visiblePatients.filter((patient) => !patient.newlyAdded)
                                          .length > 1 && (
                                          <button
                                            className="text-text-secondary disabled:text-text-disabled flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 disabled:cursor-not-allowed"
                                            type="button"
                                            onClick={() => {
                                              setFadingOutIndex(patientMatch.matchIndex)
                                              setTimeout(() => {
                                                const isSelected =
                                                  formStore.values.match.selectedMatchIndex ===
                                                  patientMatch.matchIndex
                                                if (isSelected) {
                                                  form.setFieldValue('match', {
                                                    ...formStore.values.match,
                                                    matchStatus: null,
                                                    selectedMatchIndex: undefined,
                                                  })
                                                }
                                                form.setFieldValue('hiddenMatchIndexes', [
                                                  ...hiddenMatchIndexes,
                                                  patientMatch.matchIndex,
                                                ])
                                                setHistoryValues({
                                                  match: isSelected
                                                    ? {
                                                        ...formStore.values.match,
                                                        matchStatus: null,
                                                        selectedMatchIndex: undefined,
                                                      }
                                                    : formStore.values.match,
                                                  hiddenMatchIndexes: [
                                                    ...hiddenMatchIndexes,
                                                    patientMatch.matchIndex,
                                                  ],
                                                })
                                                setFadingOutIndex(null)
                                              }, 300)
                                            }}
                                          >
                                            <Text appearance="BODY2">Hide</Text>
                                            <Icon
                                              className="text-[18px]"
                                              children="visibility_off"
                                            />
                                          </button>
                                        )}
                                      {patientMatch.newlyAdded && (
                                        <button
                                          className="text-text-secondary flex cursor-pointer items-center gap-1 border-none bg-transparent p-0"
                                          type="button"
                                          onClick={() => {
                                            setIsNewMatchVisible(false)
                                            setFadingOutIndex(patientMatch.matchIndex)
                                            setTimeout(() => {
                                              const isSelected =
                                                formStore.values.match.selectedMatchIndex ===
                                                patientMatch.matchIndex
                                              if (isSelected) {
                                                form.setFieldValue('match', {
                                                  ...formStore.values.match,
                                                  matchStatus: null,
                                                  selectedMatchIndex: undefined,
                                                })
                                              }
                                              form.setFieldValue('newlyAddedMatch', null)
                                              setHistoryValues({
                                                match: isSelected
                                                  ? {
                                                      ...formStore.values.match,
                                                      matchStatus: null,
                                                      selectedMatchIndex: undefined,
                                                    }
                                                  : formStore.values.match,
                                                newlyAddedMatch: null,
                                              })
                                              setFadingOutIndex(null)
                                            }, 300)
                                          }}
                                        >
                                          <Text appearance="BODY2">Delete</Text>
                                          <Icon
                                            className="text-[18px]"
                                            children="delete"
                                          />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )
                              })}
                              {showInlineAdd && (
                                <button
                                  className="border-divider bg-surface-lowest flex cursor-pointer items-center justify-center overflow-hidden rounded-lg border px-4 py-3 whitespace-nowrap"
                                  type="button"
                                  onClick={() => setIsNewMatchVisible(true)}
                                >
                                  <span className="text-text-link flex items-center gap-2 text-sm font-medium tracking-[1.25px] uppercase">
                                    <Icon
                                      className="text-text-link text-[18px]"
                                      children="add"
                                    />
                                    Add new match
                                  </span>
                                </button>
                              )}
                            </div>
                          </ScrollArea>
                          {showPinnedAdd && (
                            <button
                              className={cn(
                                'border-divider bg-surface-lowest mr-8 mb-3 flex shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg border transition-shadow',
                                {
                                  'shadow-[-7px_0px_9px_-7px_rgba(0,0,0,0.1)]': cardsRowOverflows,
                                },
                              )}
                              style={{ width: `${PINNED_ADD_WIDTH}px` }}
                              type="button"
                              aria-label="Add new match"
                              onClick={() => setIsNewMatchVisible(true)}
                            >
                              <Icon
                                className="text-text-link text-[24px]"
                                children="add"
                              />
                            </button>
                          )}
                        </div>
                      </>
                    )
                  }}
                />
              )}

              {isPaMatchingCutover &&
              !hasNewlyAddedMatch &&
              (!paMatchesData || (paMatchesData.patient_data ?? []).length === 0) ? (
                <div className="flex grow px-8">
                  <div className="mt-[108px] flex h-[400px] w-full flex-col items-center justify-center pb-4 text-center">
                    <SearchDocIllustration className="text-text-primary" />
                    <div className="mt-6 flex flex-col items-center gap-4">
                      <Text
                        className="text-text-secondary"
                        appearance="H6"
                      >
                        No matches found
                      </Text>
                      <Text
                        className="text-content-high-emphasis w-[380px]"
                        appearance="BODY2"
                      >
                        opti couldn't find a PA case match. Create a new one or choose No Match to
                        proceed.
                      </Text>
                    </div>
                    <Button
                      className="mt-8"
                      materialIcon="add"
                      onClick={() => setIsNewMatchVisible(true)}
                    >
                      Create new match
                    </Button>
                  </div>
                </div>
              ) : paMatchesData ? (
                <PaMatchesSection
                  data={paMatchesData}
                  containerWidth={rowWidth}
                  selectable={isPaMatchingCutover}
                  selectedIndex={formStore.values.match.selectedMatchIndex}
                  onSelect={(index) => {
                    const matchValue = {
                      matchStatus: 'validated' as apiClient.MatchStatus,
                      selectedMatchIndex: index,
                      limsCaseId: '',
                      visitId: '',
                    }
                    form.setFieldValue('match', matchValue)
                    setHistoryValues({ match: matchValue })
                  }}
                  onAddNewMatch={
                    isPaMatchingCutover && !hasNewlyAddedMatch
                      ? () => setIsNewMatchVisible(true)
                      : undefined
                  }
                  newlyAddedMatch={
                    isPaMatchingCutover ? formStore.values.newlyAddedMatch : undefined
                  }
                  onRemoveNewMatch={
                    isPaMatchingCutover && hasNewlyAddedMatch
                      ? () => {
                          form.setFieldValue('newlyAddedMatch', null)
                          const matchValue = {
                            matchStatus: null as apiClient.MatchStatus | null,
                            selectedMatchIndex: undefined,
                            limsCaseId: '',
                            visitId: '',
                          }
                          form.setFieldValue('match', matchValue)
                          setHistoryValues({ newlyAddedMatch: null, match: matchValue })
                        }
                      : undefined
                  }
                  documentFields={documentFields}
                  onClearSelection={
                    isPaMatchingCutover
                      ? () => {
                          const matchValue = {
                            matchStatus: null as apiClient.MatchStatus | null,
                            selectedMatchIndex: undefined,
                            limsCaseId: '',
                            visitId: '',
                          }
                          form.setFieldValue('match', matchValue)
                          setHistoryValues({ match: matchValue })
                        }
                      : undefined
                  }
                  isPaCardRedesignEnabled={isPaCardRedesignEnabled}
                  showContradictions={showAllContradictions}
                  hoveredField={isPaCardRedesignEnabled ? hoveredField : null}
                  connectUrlMap={connectUrlMap}
                  isMatchingV2={isPaMatchingV2}
                />
              ) : null}

              {isClaimCardRedesign && !hasNewlyAddedMatch && (patientMatches ?? []).length === 0 ? (
                <div className="flex grow px-8">
                  <div className="mt-[108px] flex h-[400px] w-full flex-col items-center justify-center pb-4 text-center">
                    <SearchDocIllustration className="text-text-primary" />
                    <div className="mt-6 flex flex-col items-center gap-4">
                      <Text
                        className="text-text-secondary"
                        appearance="H6"
                      >
                        No matches found
                      </Text>
                      <Text
                        className="text-content-high-emphasis w-[380px]"
                        appearance="BODY2"
                      >
                        opti couldn't find a match. Create a new one or choose No Match to proceed.
                      </Text>
                    </div>
                    <Button
                      className="mt-8"
                      materialIcon="add"
                      onClick={() => setIsNewMatchVisible(true)}
                    >
                      Create new match
                    </Button>
                  </div>
                </div>
              ) : isClaimCardRedesign ? (
                <ClaimMatchesSection
                  patientData={patientMatches ?? []}
                  containerWidth={rowWidth}
                  selectedMatchIndex={selectedMatchIndex}
                  selectedIndex={formStore.values.match.selectedMatchIndex}
                  onSelect={(index) => {
                    const matchValue = {
                      matchStatus: 'validated' as apiClient.MatchStatus,
                      selectedMatchIndex: index,
                      limsCaseId: '',
                      visitId: '',
                    }
                    form.setFieldValue('match', matchValue)
                    setHistoryValues({ match: matchValue })
                  }}
                  onAddNewMatch={!hasNewlyAddedMatch ? () => setIsNewMatchVisible(true) : undefined}
                  newlyAddedMatch={formStore.values.newlyAddedMatch}
                  onRemoveNewMatch={
                    hasNewlyAddedMatch
                      ? () => {
                          form.setFieldValue('newlyAddedMatch', null)
                          const matchValue = {
                            matchStatus: null as apiClient.MatchStatus | null,
                            selectedMatchIndex: undefined,
                            limsCaseId: '',
                            visitId: '',
                          }
                          form.setFieldValue('match', matchValue)
                          setHistoryValues({ newlyAddedMatch: null, match: matchValue })
                        }
                      : undefined
                  }
                  documentFields={documentFields}
                  onClearSelection={() => {
                    const matchValue = {
                      matchStatus: null as apiClient.MatchStatus | null,
                      selectedMatchIndex: undefined,
                      limsCaseId: '',
                      visitId: '',
                    }
                    form.setFieldValue('match', matchValue)
                    setHistoryValues({ match: matchValue })
                  }}
                  showContradictions={showAllContradictions}
                  hoveredField={hoveredField}
                  currentPage={claimCurrentPage}
                  isMatchingV2={isClaimMatchingV2}
                />
              ) : null}

              <div className={cn('px-8', { '-mt-2': isPaMatchingCutover || isClaimCardRedesign })}>
                {!isPaMatchingCutover &&
                  !isClaimCardRedesign &&
                  patients.filter((patient) => !patient.newlyAdded).length > 0 && (
                    <Text
                      className="mt-2 py-1.5"
                      appearance="SUBTITLE"
                    >
                      Didn't Find a Patient?
                    </Text>
                  )}
                <form.Field
                  name="match"
                  children={(field) => {
                    function handleChange() {
                      if (field.state.value.matchStatus !== 'no_match') {
                        field.handleChange({
                          ...field.state.value,
                          matchStatus: 'no_match',
                          selectedMatchIndex: undefined,
                        })
                        setHistoryValues({
                          match: {
                            ...field.state.value,
                            matchStatus: 'no_match',
                            selectedMatchIndex: undefined,
                          },
                        })
                      }
                      textFieldRef.current?.focus()
                    }

                    return (
                      <div
                        className="flex min-h-[60px] gap-6"
                        data-track-name="no_match"
                      >
                        <RadioButton
                          label="No match"
                          withinButton
                          checked={field.state.value.matchStatus === 'no_match'}
                          onChange={handleChange}
                        />

                        {/* Backwards compatibility for user_suggested_lims_case_file_id and user_suggested_patient_account_number */}
                        {!!userSuggestedLimsCaseFileId || !!userSuggestedPatientAccountNumber ? (
                          <div className="flex gap-12 px-6 py-4">
                            <div>
                              {!!userSuggestedPatientAccountNumber && (
                                <div className="flex flex-col items-start">
                                  <Text
                                    className="text-text-secondary"
                                    appearance="BODY2"
                                  >
                                    Enter the Visit ID if you have it
                                  </Text>
                                  <Textfield
                                    textfieldRef={textFieldRef}
                                    className="mt-1 w-[300px]"
                                    label="Visit ID"
                                    disabled={field.state.value.matchStatus !== 'no_match'}
                                    dense
                                    value={field.state.value.visitId}
                                    onChange={(e) => {
                                      field.handleChange({
                                        ...field.state.value,
                                        visitId: e.target.value,
                                      })
                                    }}
                                    onBlur={() => {
                                      if (
                                        field.state.value.visitId !== historyValues.match.visitId
                                      ) {
                                        setHistoryValues({
                                          match: {
                                            ...field.state.value,
                                            visitId: field.state.value.visitId,
                                          },
                                        })
                                      }
                                    }}
                                    helperText="AKA: Patient Account Number"
                                  />
                                </div>
                              )}
                              {!!userSuggestedLimsCaseFileId && (
                                <div className="flex flex-col items-start">
                                  <Text
                                    className="text-text-secondary"
                                    appearance="BODY2"
                                  >
                                    Enter the Case Number if you have it
                                  </Text>
                                  <Textfield
                                    textfieldRef={textFieldRef}
                                    className="mt-1 w-[300px]"
                                    label="Case Number"
                                    disabled={field.state.value.matchStatus !== 'no_match'}
                                    dense
                                    value={field.state.value.limsCaseId}
                                    onChange={(e) => {
                                      field.handleChange({
                                        ...field.state.value,
                                        limsCaseId: e.target.value,
                                      })
                                    }}
                                    onBlur={() => {
                                      setHistoryValues({
                                        match: {
                                          ...field.state.value,
                                          limsCaseId: field.state.value.limsCaseId,
                                        },
                                      })
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                            <div className="flex flex-1 flex-col items-start">
                              <Text
                                className="text-text-secondary"
                                appearance="BODY2"
                              >
                                No {userSuggestedPatientAccountNumber ? 'Visit ID' : 'Case Number'}?
                                Add a short note here to explain your choice
                              </Text>
                              <form.Field
                                name="notes"
                                children={(field) => {
                                  return (
                                    <Textarea
                                      className="mt-3 h-[40px] w-full resize-none"
                                      placeholder="Type here"
                                      filled
                                      disabled={form.state.values.match.matchStatus !== 'no_match'}
                                      value={field.state.value}
                                      onChange={(e) => field.handleChange(e.target.value)}
                                      onBlur={(e) => {
                                        field.handleBlur()
                                        setHistoryValues({ notes: e.target.value })
                                      }}
                                    />
                                  )
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <form.Field
                            name="notes"
                            children={(field) => {
                              if (formStore.values.match.matchStatus !== 'no_match') {
                                return null
                              }
                              return (
                                <Textarea
                                  className="h-[40px] max-w-[344px] resize-none"
                                  placeholder="Add a short note here to explain your choice"
                                  filled
                                  value={field.state.value}
                                  onChange={(e) => field.handleChange(e.target.value)}
                                  onBlur={(e) => {
                                    field.handleBlur()
                                    setHistoryValues({ notes: e.target.value })
                                  }}
                                />
                              )
                            }}
                          />
                        )}
                      </div>
                    )
                  }}
                />
              </div>
            </div>
            {sentinelElementBottom}
          </form>
        </ScrollArea>
        <form.Subscribe
          selector={(state) => ({
            isDirty: state.isDirty,
            isSubmitting: state.isSubmitting,
            isValid: state.isValid,
            notesRequired: !state.fieldMeta.notes?.isValid,
            isDefaultValue: state.isDefaultValue,
          })}
          children={({ isDirty, isSubmitting, isValid, notesRequired, isDefaultValue }) => (
            <ReviewActions
              className="shrink-0 px-2"
              isDirty={isDirty}
              isSubmitDisabled={
                stepFlag.hasActiveStepFlag
                  ? false
                  : isSubmitting ||
                    !isValid ||
                    document.review_status === DOCUMENT_SERVER_STATUS.processing ||
                    (!wasReclassified &&
                      getIsPatientFinished(document, subcategories) &&
                      isDefaultValue)
              }
              onSubmit={
                stepFlag.hasActiveStepFlag
                  ? () => navigate(getDocumentsUrlWithCurrentParams())
                  : form.handleSubmit
              }
              submitLabel={stepFlag.hasActiveStepFlag ? 'Return to Work Queue' : 'Validate'}
              submitTooltip={
                stepFlag.hasActiveStepFlag
                  ? undefined
                  : notesRequired
                    ? 'Explain why you select No Match'
                    : !isValid
                      ? 'Please select a match or "No Match".'
                      : undefined
              }
              canUndo={canUndo}
              canRedo={canRedo}
              onRevert={revert}
              onUndo={undo}
              onRedo={redo}
              previousStepLabel={isTaggingEnabled ? 'Go to tagging' : 'Go to data extraction'}
              previousStepUrl={
                isTaggingEnabled
                  ? `/documents/${document.key}/tagging${Object.keys(queryParams).length ? `?${new URLSearchParams(queryParams).toString()}` : ''}`
                  : getDocumentStepUrl(document.key, 'data', { queryParams })
              }
              isError={isPatientMatchingReviewError}
              errorContent="opti ran into an issue while confirming the patient match. Please refresh the page and try again."
              isScrolled={isScrolledBottom}
              compactWhenAuditTrailVisible
            />
          )}
        />
      </div>
    )
  },
})
